import { renderToString } from "react-dom/server";
import TableElementReadOnly from "./components/preview/TableElementReadOnly";
import ExpertsReadOnly from "./components/preview/ExpertsReadOnly";
import CustomError from "./error";
import showdown from "showdown";
import dayjs from "dayjs";
import { FORM_TYPES, RULE_CONDITION_TYPES } from "./constants";
import collect from "collect.js";
import { invert } from "lodash";

export const generateListLastYears = (reportYear, yrs, minYear = "") => {
    let options = [];
    if (minYear !== "") {
        yrs = reportYear - minYear;
    }
    for (let i = reportYear; i >= reportYear - yrs; i--) {
        options.push(
            <option value={i} key={i}>
                {i}
            </option>
        );
    }
    return options;
};

export const validateEmail = (email) => {
    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

export const isMatchInArr = (arr, matchId, matchArr = [], checkKey = "ref_id") => {
    const check = arr.find((r) => parseInt(r.id) === parseInt(matchId));
    return check && matchArr.includes(check[checkKey]);
};

export const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
};

const handleProcessVariableValidations = (variableObj = null, string = "", throwError = false) => {
    if (variableObj === null || variableObj === undefined) {
        if (throwError) {
            return new CustomError("-- Error: Variables are invalid --", "error");
        } else {
            return "-- Error Processing Variables [1] --";
        }
    }
    if (variableObj.status === "uninitialized") {
        if (throwError) {
            throw new CustomError("-- Warning: Variables are uninitialised --", "warning");
        } else {
            return string;
        }
    }
    if (variableObj.data === undefined) {
        if (throwError) {
            throw new CustomError("-- Warning: Variables Data is unavailable --", "warning");
        } else {
            return "-- Variables Data is unavailable --";
        }
    }
    // if (variableObj.isLoading) {
    //     if (throwError) {
    //         throw new CustomError("-- Warning: Variables are loading --", "warning");
    //     } else {
    //         return "-- Loading --";
    //     }
    // }
    // if (variableObj.isFetching) {
    //     if (throwError) {
    //         throw new CustomError("-- Warning: Variables are fetching --", "warning");
    //     } else {
    //         return "-- Fetching --";
    //     }
    // }
    if (variableObj.isError) {
        console.error({ error: variableObj.error });
        if (throwError) {
            return new CustomError("-- Error: Processing Variables --", "error");
        } else {
            return "-- Error Processing Variables [2] --";
        }
    }
    if (!variableObj.isLoading && !variableObj.isSuccess) {
        if (throwError) {
            return new CustomError("-- Error: Variables error --", "error");
        } else {
            return "-- Error Processing Variables [3] --";
        }
    }
    return null;
}

export const processVariables = (variableObj = null, string = "", throwError = false, processEmptyValue = true) => {
    const validations = handleProcessVariableValidations(variableObj, string, throwError);
    if (validations) return validations; 
    for (const x in variableObj.data) {
        if (typeof variableObj.data[x] === "object") {
            if (variableObj.data[x].type === "form_table") {
                string = String(string).replaceAll(
                    new RegExp(escapeRegExp(x), "g"),
                    renderToString(
                        <TableElementReadOnly data={variableObj.data[x]?.data} columns={variableObj.data[x]?.columns} />
                    )
                );
            }
            if (variableObj.data[x].type === "experts") {
                string = String(string).replaceAll(
                    new RegExp(escapeRegExp(x), "g"),
                    renderToString(<ExpertsReadOnly data={variableObj.data[x]?.data} />)
                );
            }
        } else {
            string = String(string).replaceAll(new RegExp(escapeRegExp(x), "g"), variableObj.data[x]);
        }
    }
    if (string !== "") {
        if ((new RegExp("{[0-9]{1,6}}", "g")).test(string)) {
            if (throwError) {
                return new CustomError("-- Error: Invalid Variable --", "error");
            } else {
                // console.error({ errorCode: 'Invalid Variable', variableObj, string, throwError, processEmptyValue });
                return string.replaceAll(new RegExp("{[0-9]{1,6}}", "g"), "-- Error: Invalid Variable --");
            }
        }
        return string;
    }
    if (throwError) {
        return new CustomError("-- Error: No Data for variables --", "error");
    } else {
        if (processEmptyValue) {
            console.error({ errorCode: 4, variableObj, string, throwError, processEmptyValue });
            return "-- Error Processing Variables [4] --";
        }
        return string;
    }
};

export const getVariableValue = (variableObj = null, string = "", throwError = false) => {
    const validations = handleProcessVariableValidations(variableObj, string, throwError);
    if (validations) return validations;
    return variableObj.data[string] ?? '';
}

export const isJSON = (str) => {
    try {
        return JSON.parse(str) && !!str;
    } catch (e) {
        return false;
    }
};

export const doAtob = (str, defaultValue = "") => {
    try {
        return atob(str) || defaultValue;
    } catch (e) {
        return defaultValue;
    }
};

export const setObjectViaDotPath = (object, path, value) => {
    object = { ...object };
    const keys = path.split(".");
    if (keys.length === 1) object[keys[0]] = value;
    else if (keys.length !== 0) {
        if (object[keys[0]]) object[keys[0]] = setObjectViaDotPath(object[keys[0]], keys.slice(1), value);
        else {
            object[keys[0]] = {};
            object[keys[0]] = setObjectViaDotPath(object[keys[0]], keys.slice(1), value);
        }
    }
    return object;
};

export const getElementsFlattened = (obj) => {
    const ret = {};
    for (const y of Object.values(obj)) {
        for (const [k, x] of Object.entries(y.children)) {
            ret[k] = x;
        }
    }
    return ret;
};
export const pluralize = (count, noun, suffix = "s") => `${noun}${count !== 1 ? suffix : ""}`;
export const isNumeric = (string) => /^[+-]?\d+(\.\d+)?$/.test(string);

export const forceInputLowercase = (e, preventSpace = false) => {
    let el = e.target;
    let start = el.selectionStart;
    let end = el.selectionEnd;
    el.value = el.value.toLowerCase();
    if (preventSpace) {
        el.value = String(el.value).replace(new RegExp(" ", "g"), "_");
    }
    el.setSelectionRange(start, end);
};
export const mdToHtml = (text) => {
    const converter = new showdown.Converter();
    return converter.makeHtml(text);
};
export const flipKeyValueInObject = (obj) =>
    Object.fromEntries(Object.entries(obj).map(([key, value]) => [value, key]));
export const removeCharsFromString = (string, regExps = [], replaceValue = '') => {
    for (const x of regExps) {
        string = string.replaceAll(new RegExp(x, 'g'), replaceValue)
    }
    return string;
}

export const shouldHaveObjectData = (dataElementType, listAppearance, multipleSelection) => {
    return (dataElementType === "list" && listAppearance === "dropdown" && !multipleSelection) ||
        (dataElementType === "list" && listAppearance === "checkboxes") ||
        dataElementType === "radio_selection";
}
export const processDataElementValue = (dataElementType, id, listAppearance, multipleSelection, elementsData = [], defaultData = "", saveDefaultDataCallback = () => {}) => {
    if (elementsData && id) {
        const val =
            Array.isArray(elementsData[id]) && elementsData[id].length > 0
                ? elementsData[id].find(item => item?.content != null)
                : elementsData[id];

        if (val !== undefined) {
            if (dataElementType === "date") {
                const tmp =
                    !["", undefined].includes(val?.content) && dayjs(val?.content)?.isValid()
                        ? dayjs(val?.content)
                        : defaultData === ""
                        ? ""
                        : defaultData;
                return tmp?.length === 0 ? "" : tmp;
            } else if (shouldHaveObjectData(dataElementType, listAppearance, multipleSelection)) {
                return !["", null, undefined].includes(val) ? val : defaultData;
            } else {
                return !["", null, undefined].includes(val?.content) ? val?.content : defaultData;
            }
        }
    }
    saveDefaultDataCallback(defaultData);
    return defaultData;
}
export const getRuleConditionOptions = (cat = []) => {
    if (!(Array.isArray(cat) && cat.length > 0))
        throw new Error('Invalid Rule Condition Type/s');
    let ret = new Array();
    for (const x of cat) {
        if (x) {
            ret = ret.concat(RULE_CONDITION_TYPES[x] ?? []);
        }
    }
    return ret;
}
export const getFormTypesFromRef = (ref) => invert(FORM_TYPES)[ref] ?? undefined;

export const returnLanguageById = (id = '') => {
    switch (id) {
        case 4:
            return "Spanish";
        case 5:
            return "French"
        case '':
        case null:
        case undefined:
            return "";
        default:
            return "English";
    }
};
